import { useForm } from "react-hook-form";
import { BaseFormControlledModal } from "../../../../../../common/components/base_form_controlled_modal";
import { zodResolver } from "@hookform/resolvers/zod";
import { RequisicaoProdutoServicoForm, RequisicaoProdutoServicoSchema } from "../../../schemas/RequisicaoServicosProdutos_cadastro/requisicao_produto_servico";
import { RequisicaoProdutoServicoLista } from "../../../../domain/models/requisicao_produto_servico_lista";
import { useEffect, useMemo } from "react";
import { DefaultFormSelectInput } from "../../../../../../common/components/default_form_select_input";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { ProdutoServicoDropDown } from "../../../../../../common/domain/models/produto_servico_dropdown";
import { DefaultFormNumericInput } from "../../../../../../common/components/default_form_numeric_input";
import { SubcontaDropdown } from "../../../../../rh/domain/models/subconta_dropdown";

/**
 * RequisicaoProdutoServicoModal
 */

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  produtosServicosDropdown: ProdutoServicoDropDown[];
  subcontasDropdown: SubcontaDropdown[];
  selectedRequisicaoProdutoServico: RequisicaoProdutoServicoLista | null;
  onClose: () => void;
  onSubmit: (form: RequisicaoProdutoServicoForm) => void;
  onNewProdutoServico: (callback: (idProdutoServico: number) => void) => void;
};

export const RequisicaoProdutoServicoModal = ({
  open,
  saving,
  loading,
  produtosServicosDropdown,
  subcontasDropdown,
  selectedRequisicaoProdutoServico,
  onClose,
  onSubmit,
  onNewProdutoServico,
}: Props) => {
  const hookForm = useForm<RequisicaoProdutoServicoForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: { tipo: 2 },
    resolver: zodResolver(RequisicaoProdutoServicoSchema),
  });

  const [tipo] = hookForm.watch(['tipo']);

  const filtroProdutosServicosOptions = useMemo(() => {
    return [
      {
        id: 2,
        label: 'Produtos/Serviços',
      },
      {
        id: 0,
        label: 'Produtos',
      },
      {
        id: 1,
        label: 'Serviços',
      },
    ];
  }, [
  ]);

  const filteredProdutosServicos = useMemo(() => {
    const activeProdutosServicosDropdown = produtosServicosDropdown.filter(x => x.ativo);
    if (tipo === 2) return activeProdutosServicosDropdown;
    if (tipo === 0) return activeProdutosServicosDropdown.filter(x => x.tipo === 'P');
    return activeProdutosServicosDropdown.filter(x => x.tipo === 'S');
  }, [
    tipo,
    produtosServicosDropdown,
  ]);

  useEffect(() => {
    if (open) {
      if (selectedRequisicaoProdutoServico) {
        const quantidade = selectedRequisicaoProdutoServico.quantidade ?? 0;

        hookForm.resetField('tipo', { defaultValue: 2 });
        hookForm.setValue('idProdutoServico', selectedRequisicaoProdutoServico.idProdutoServico ?? 0);
        hookForm.setValue('idSubconta', selectedRequisicaoProdutoServico.idSubconta);
        hookForm.setValue('quantidade', quantidade);
      } else {
        hookForm.reset({ tipo: 2 });
      }
    }
  }, [
    hookForm,
    open,
    selectedRequisicaoProdutoServico,
  ]);

  return (
    <BaseFormControlledModal
      open={open}
      saving={saving}
      loading={loading}
      title="Requisição Produtos/Serviços"
      width="65rem"
      onClose={onClose}
      onSubmit={hookForm.handleSubmit(onSubmit)}>
      <>
        <div className="w-full flex flex-col gap-2 my-2">
          <div className="w-full flex flex-row">
            <div className="w-[15rem]">
              <DefaultFormSelectInput
                name='tipo'
                label='Tipo'
                options={filtroProdutosServicosOptions}
                showSelectionLabel={false}
                onChangeValue={() => hookForm.resetField('idProdutoServico')}
                reactHookForm={hookForm}
              />
            </div>

            <div className="w-full flex-1">
              <DefaultFormAutocomplete
                name='idProdutoServico'
                label='Produto/Serviço'
                options={filteredProdutosServicos}
                getOptionKey={x => x.idProdutoServico}
                getOptionLabel={x => `${x.codigo} - ${x.descricao}`}
                reactHookForm={hookForm}
                onNew={() => {
                  onNewProdutoServico(
                    idProdutoServico => hookForm.setValue('idProdutoServico', idProdutoServico)
                  );
                }}
              />
            </div>
          </div>

          <DefaultFormNumericInput
            name='quantidade'
            label='Quantidade'
            reactHookForm={hookForm}
          />

          <DefaultFormAutocomplete
            name='idSubconta'
            label='Subconta'
            options={subcontasDropdown}
            getOptionKey={x => x.id}
            getOptionLabel={x => x.title}
            reactHookForm={hookForm}
          />
        </div>
      </>
    </BaseFormControlledModal>
  );
}